import { useEffect, useRef, type RefObject } from "react";

import { CURSOR_TRACKING } from "../constants";

/** Accepts HTML or SVG elements — both expose `style` and getBoundingClientRect. */
type Trackable = HTMLElement | SVGElement;

interface UseCursorTrackingArgs {
  anchorRef: RefObject<HTMLElement | null>;
  headRef: RefObject<Trackable | null>;
  /** Optional — eye elements to translate toward the cursor (unused for the SVG face). */
  leftPupilRef?: RefObject<Trackable | null>;
  rightPupilRef?: RefObject<Trackable | null>;
  enabled?: boolean;
}

const resetTransform = (el: Trackable | null) => {
  if (el) el.style.transform = "";
};

export const useCursorTracking = ({
  anchorRef,
  headRef,
  leftPupilRef,
  rightPupilRef,
  enabled = true,
}: UseCursorTrackingArgs): void => {
  const targetRef = useRef({ x: 0, y: 0 });
  const currentRef = useRef({ x: 0, y: 0 });

  useEffect(() => {
    if (!enabled) {
      resetTransform(headRef.current);
      resetTransform(leftPupilRef?.current ?? null);
      resetTransform(rightPupilRef?.current ?? null);
      return;
    }

    const handleMove = (e: MouseEvent) => {
      const el = anchorRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      const dx = e.clientX - cx;
      const dy = e.clientY - cy;
      // Direction × how far off-centre the cursor is, saturating at `reach`.
      // Previously this was `{ cos(angle), sin(angle) }` — a UNIT vector, so the
      // magnitude was always 1 and the eyes/head sat pinned at MAXIMUM deflection
      // for any cursor position anywhere on the page, and never relaxed once the
      // pointer had moved. That is what made the eyes look shoved out of the
      // visor even with the pointer parked far away.
      const distance = Math.hypot(dx, dy);
      if (distance < 1) {
        targetRef.current = { x: 0, y: 0 };
        return;
      }
      const strength = Math.min(distance / CURSOR_TRACKING.reach, 1);
      targetRef.current = {
        x: (dx / distance) * strength,
        y: (dy / distance) * strength,
      };
    };

    let raf = 0;
    const tick = () => {
      const t = targetRef.current;
      const c = currentRef.current;
      c.x += (t.x - c.x) * CURSOR_TRACKING.smoothing;
      c.y += (t.y - c.y) * CURSOR_TRACKING.smoothing;

      const px = c.x * CURSOR_TRACKING.maxPupilOffset;
      const py = c.y * CURSOR_TRACKING.maxPupilOffset;

      if (leftPupilRef?.current) {
        leftPupilRef.current.style.transform = `translate(${px}px, ${py}px)`;
      }
      if (rightPupilRef?.current) {
        rightPupilRef.current.style.transform = `translate(${px}px, ${py}px)`;
      }
      if (headRef.current) {
        headRef.current.style.transform = `rotate(${c.x * CURSOR_TRACKING.maxHeadTilt}deg)`;
      }
      raf = requestAnimationFrame(tick);
    };

    window.addEventListener("mousemove", handleMove, { passive: true });
    raf = requestAnimationFrame(tick);

    return () => {
      window.removeEventListener("mousemove", handleMove);
      cancelAnimationFrame(raf);
    };
  }, [anchorRef, headRef, leftPupilRef, rightPupilRef, enabled]);
};
